import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { LoggerService } from '../../../infra/logger/logger.service';
import { MicroserviceHttpService } from '../../http/microservice-http.service';
import { InLineControlModel } from 'src/app/models/api/inLineControl.model';
import { FindingModel, DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { environment } from 'src/environments/environment';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { InLineActivityStepModel } from 'src/app/models/api/inLineActivityStep.model';
import { AiService } from 'src/app/models/api/agentStep.model';

export type ActivityKind = 'steps' | 'findings';
export type AutocompleteField = 'actor' | 'agent' | 'clientApplication';

export interface AutocompleteSuggestion {
  value: string;
  count: number;
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[];
}

export interface ActivityCountResponse {
  count: number;
}

const CLAUDE_CODE = 'claudeCode';
const CLAUDE_DESKTOP = 'claudeDesktop';
const claudeAiServices = [CLAUDE_CODE, CLAUDE_DESKTOP];

@Injectable({
  providedIn: 'root',
})
export class SearchActivityHttpService {
  public readonly defaultPageSize: number = 50;
  private readonly _httpService: MicroserviceHttpService;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _logger: LoggerService
  ) {
    this._httpService = new MicroserviceHttpService(
      this._httpClient,
      'ai-edge-manager' as keyof typeof environment.services,
      this._logger
    );
  }

  /**
   * @public Start getting first page of inventory resources
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public startGetFindings(
    sortData?: SortColumn,
    filterOptions?: ISelectedFilters,
    pageSize = this.defaultPageSize
  ): Observable<JsonHalCollection<InLineControlModel>> {
    const url = 'activity/findings';
    const httpParams = this._createActivityQueryParams(pageSize, sortData, filterOptions);
    return this._httpService.get<JsonHalCollection<InLineControlModel>>(url, undefined, httpParams);
  }

  /**
   * Convenience: fetch findings for a specific thread id with sorting and page size
   */
  public getInLineFindingsByThreadId(threadId: string): Observable<JsonHalCollection<FindingModel>> {
    const url = 'activity/findings';
    let httpParams = new HttpParams().set('pageSize', this.defaultPageSize).set('threadId', threadId);
    httpParams = httpParams.set('sortBy', 'timestamp(desc)');
    return this._httpService.get<JsonHalCollection<InLineControlModel>>(url, undefined, httpParams).pipe(
      map(
        (res) =>
          ({
            ...res,
            _embedded: {
              items: (res._embedded?.items ?? []).map((i) => this._mapInlineFindingToFinding(i)),
            },
          }) as unknown as JsonHalCollection<FindingModel>
      )
    );
  }

  private _mapInlineFindingToFinding(model: InLineControlModel): FindingModel {
    const severity = (model?.rule?.severity as DetectionRuleSeverity) ?? DetectionRuleSeverity.informational;
    return {
      id: model.id,
      rule: { severity } as unknown as FindingModel['rule'],
      thread: { id: model.threadId } as unknown as FindingModel['thread'],
      integration: { id: model.integrationId } as unknown as FindingModel['integration'],
      zenityAccountId: '' as unknown as FindingModel['zenityAccountId'],
      timestamp: new Date(model.timestamp),
      relatedSteps: [],
      evidence: (model.evidence ?? []) as FindingModel['evidence'],
      mainActor: { displayName: model.actor?.displayName ?? '' } as unknown as FindingModel['mainActor'],
      labelBindings: [],
    } as FindingModel;
  }

  /**
   * @public Continue getting inventory resources using a continuation token
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public getFindingsByContinuationToken(
    continuationToken: string,
    filterOptions?: ISelectedFilters
  ): Observable<JsonHalCollection<InLineControlModel>> {
    const url = 'activity/findings';
    let httpParams = new HttpParams().append('continuationToken', continuationToken);

    if (filterOptions) {
      httpParams = this._appendInLineControlFilterQueryParams(httpParams, filterOptions);
    }

    return this._httpService.get<JsonHalCollection<InLineControlModel>>(url, undefined, httpParams);
  }

  /**
   * @public Start getting first page of in-line control activity steps
   * @param continuationToken Optional continuation token for pagination
   * @param sortData Optional sort configuration
   * @param filterOptions Optional filter options (supports sinceTimestamp and untilTimestamp)
   * @param pageSize Page size for results
   * @returns an Observable{JsonHalCollection<InLineActivityStepModel>} with the resources returned from server
   */
  public getInLineControlActivitySteps(
    continuationToken?: string,
    sortData?: SortColumn,
    filterOptions?: ISelectedFilters,
    pageSize = this.defaultPageSize
  ): Observable<JsonHalCollection<InLineActivityStepModel>> {
    const url = 'activity/steps';
    if (continuationToken) {
      let httpParams = new HttpParams().append('continuationToken', continuationToken);

      // Apply filters even when using continuation token to maintain filter consistency
      if (filterOptions) {
        httpParams = this._appendInLineControlFilterQueryParams(httpParams, filterOptions);
      }

      // Apply sorting when using continuation token
      if (sortData) {
        httpParams = this._appendActivitySortQueryParams(httpParams, sortData);
      }

      return this._httpService.get<JsonHalCollection<InLineActivityStepModel>>(url, undefined, httpParams);
    }

    const httpParams = this._createActivityQueryParams(pageSize, sortData, filterOptions);
    return this._httpService.get<JsonHalCollection<InLineActivityStepModel>>(url, undefined, httpParams);
  }

  /**
   * @public Find single in-line activity step by id using query param (id=:stepId)
   * Some environments expose step filtering via query params rather than path id.
   */
  public findInLineActivityStepByIdViaQuery(stepId: string): Observable<InLineActivityStepModel> {
    const url = 'activity/steps';
    const httpParams = new HttpParams().set('id', stepId).set('pageSize', 1);
    return this._httpService.get<JsonHalCollection<InLineActivityStepModel>>(url, undefined, httpParams).pipe(
      map((res: JsonHalCollection<InLineActivityStepModel>) => {
        const embedded = (res as { _embedded?: { items?: InLineActivityStepModel[] } })._embedded;
        const items = embedded?.items ?? [];
        return items[0] as InLineActivityStepModel;
      })
    );
  }

  /**
   * @public Fetch a single in-line control finding by its id
   * @param findingId The finding id to look up
   * @returns an Observable{InLineControlModel} with the single finding
   */
  public getInLineControlFindingById(findingId: string): Observable<InLineControlModel | undefined> {
    const url = 'activity/findings';
    const httpParams = new HttpParams().set('id', findingId).set('pageSize', 1);
    return this._httpService.get<JsonHalCollection<InLineControlModel>>(url, undefined, httpParams).pipe(
      map((res: JsonHalCollection<InLineControlModel>) => {
        const items = res._embedded?.items ?? [];
        return items[0];
      })
    );
  }

  /**
   * Get autocomplete suggestions for activity filter fields
   * @param kind Activity type: 'steps' or 'findings'
   * @param field Field to autocomplete: 'actor', 'agent', 'clientApplication'
   * @param prefix Search prefix (minimum 1 char required by API)
   * @returns Observable with array of suggestion values
   */
  public getAutocompleteOptions(
    kind: ActivityKind,
    field: AutocompleteField,
    prefix: string
  ): Observable<AutocompleteResponse> {
    const url = `activity/${kind}/autocomplete`;
    const httpParams = new HttpParams().set('field', field).set('prefix', prefix);
    return this._httpService.get<AutocompleteResponse>(url, undefined, httpParams);
  }

  public getActivityCount(
    kind: ActivityKind,
    action: 'block' | 'detect',
    filterOptions?: ISelectedFilters
  ): Observable<ActivityCountResponse> {
    const url = `activity/${kind}/count`;
    let httpParams = new HttpParams().set('action', action);
    if (filterOptions) {
      httpParams = this._appendInLineControlFilterQueryParams(httpParams, filterOptions);
    }
    return this._httpService.get<ActivityCountResponse>(url, undefined, httpParams);
  }

  private _createActivityQueryParams(
    pageSize: number,
    sortData?: SortColumn,
    filterOptions?: ISelectedFilters,
    shouldLimitResultsAmount = true
  ): HttpParams {
    let httpParams = new HttpParams();
    if (shouldLimitResultsAmount) {
      httpParams = httpParams.append('pageSize', pageSize);
    }

    if (filterOptions) {
      httpParams = this._appendInLineControlFilterQueryParams(httpParams, filterOptions);
    }

    if (sortData) {
      httpParams = this._appendActivitySortQueryParams(httpParams, sortData);
    }
    return httpParams;
  }

  private _appendActivitySortQueryParams(httpParams: HttpParams, sortData: SortColumn): HttpParams {
    const sortFieldString: string | undefined = sortData.column.sortKey;
    const ascOrDescString = sortData.isDesc ? 'desc' : 'asc';

    if (sortFieldString) {
      httpParams = httpParams.append('sortBy', `${sortFieldString}(${ascOrDescString})`);
    }
    return httpParams;
  }

  private _appendInLineControlFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
    // Add text search filter
    if (filterOptions.search && filterOptions.search.trim().length > 0) {
      httpParams = httpParams.append('textSearch', filterOptions.search.trim());
    }

    // Add timestamp filters (sinceTimestamp and untilTimestamp)
    if (filterOptions.fromDate) {
      httpParams = httpParams.append('sinceTimestamp', filterOptions.fromDate.toISOString());
    }

    if (filterOptions.toDate) {
      httpParams = httpParams.append('untilTimestamp', filterOptions.toDate.toISOString());
    }

    if (filterOptions.inLineStepType && filterOptions.inLineStepType.length > 0) {
      httpParams = httpParams.append('stepType', filterOptions.inLineStepType.join(','));
    }

    if (filterOptions.inLineIntegration && filterOptions.inLineIntegration.length > 0) {
      httpParams = httpParams.append('integrationId', filterOptions.inLineIntegration.join(','));
    }

    if (filterOptions.inLineAiService && filterOptions.inLineAiService.length > 0) {
      const expandedAiServices = filterOptions.inLineAiService.flatMap((service) =>
        service === AiService.claude ? claudeAiServices : [service]
      );
      httpParams = httpParams.append('aiService', expandedAiServices.join(','));
    }

    if (filterOptions.inLineEnvironment && filterOptions.inLineEnvironment.length > 0) {
      httpParams = httpParams.append('environmentId', filterOptions.inLineEnvironment.join(','));
    }

    // Support both inLineThreadId (from In-Line Control page) and threadId (from Activity page)
    const threadIds = filterOptions.inLineThreadId || filterOptions.threadId;
    if (threadIds && threadIds.length > 0) {
      httpParams = httpParams.append('threadId', threadIds.join(','));
    }

    // New filters for In-Line Activity (steps)
    if (filterOptions.inLineActor && filterOptions.inLineActor.length > 0) {
      httpParams = httpParams.append('actor', filterOptions.inLineActor.join(','));
    }

    if (filterOptions.inLineAgent && filterOptions.inLineAgent.length > 0) {
      httpParams = httpParams.append('agent', filterOptions.inLineAgent.join(','));
    }

    if (filterOptions.inLineClientApplication && filterOptions.inLineClientApplication.length > 0) {
      httpParams = httpParams.append('clientApplication', filterOptions.inLineClientApplication.join(','));
    }

    // New filter for In-Line Control (findings)
    if (filterOptions.inLineSeverity && filterOptions.inLineSeverity.length > 0) {
      httpParams = httpParams.append('severity', filterOptions.inLineSeverity.join(','));
    }

    return httpParams;
  }
}
